import React, { CSSProperties, useState } from 'react';
import { Card, Typography, Box, makeStyles, Menu, MenuItem, Tooltip } from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import GroupsIcon from '@material-ui/icons/HomeWorkOutlined';
import { ProjectFolder, User } from '@clef/shared/types';
import { IconButton } from '@clef/client-library';
import EditFolderNameTextField from '@/components/Text/EditFolderNameTextField';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 20,
    boxShadow: 'none !important',
    cursor: 'pointer',
    position: 'relative',
    height: '100%',
    '&:hover': {
      '& $folderIcon': {
        transform: 'scale(1.1)',
      },
      '& $folderIconSmall': {
        transform: 'rotate(-20deg)',
      },
      '& $details': {
        backgroundColor: theme.palette.grey[200],
      },
    },
  },
  iconContainer: {
    overflow: 'hidden',
    marginBottom: -theme.spacing(5),
    width: '100%',
    height: 'calc(100% - 70px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#e3e8f0',
  },
  folderIcon: {
    fontSize: 80,
    color: theme.palette.grey[400],
    transition: 'transform 0.2s',
  },
  details: {
    width: '100%',
    padding: theme.spacing(4, 5),
    borderRadius: 16,
    position: 'absolute',
    bottom: 0,
    background: theme.palette.grey[100],
    transition: 'all 0.2s ease-in',
  },
  detailsHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsName: {
    maxWidth: 'calc(100% - 40px)',
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  folderIconSmall: {
    fontSize: 32,
    color: theme.palette.grey[500],
    transform: 'rotate(0deg)',
    transition: 'transform 0.2s',
  },
  createdByRow: {
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
  createdByIcon: {
    color: '#7D89AF',
    fontSize: 18,
    marginRight: theme.spacing(2),
  },
  textColor: {
    color: theme.palette.grey[600],
  },
  deleteText: {
    fontWeight: 'bold',
  },
}));

export interface FolderCardProps {
  folder: ProjectFolder;
  style?: CSSProperties;
  onClick?: (folderId: number) => void;
  onDelete?: (folder: ProjectFolder) => void;
  onMove?: (folder: ProjectFolder) => void;
  creatorUser?: User;
  isCurrentUserCreator?: boolean;
  hideMoreActionButton?: boolean;
  hideMove?: boolean;
}

export const FolderCard: React.FC<FolderCardProps> = ({
  folder,
  style,
  onClick,
  onDelete,
  onMove,
  creatorUser,
  isCurrentUserCreator,
  hideMoreActionButton = false,
  hideMove = false,
}) => {
  const styles = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    setIsEditingName(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    onDelete?.(folder);
  };

  const handleMove = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    onMove?.(folder);
  };

  return (
    <Card
      data-testid="folder_card"
      onClick={() => !isEditingName && onClick?.(folder.id)}
      className={styles.container}
      style={style}
    >
      <Box className={styles.iconContainer}>
        <FolderIcon className={styles.folderIcon} />
      </Box>

      <Box className={styles.details}>
        <Box className={styles.detailsHeader}>
          <Box className={styles.detailsName}>
            <FolderIcon className={styles.folderIconSmall} />
            {isEditingName ? (
              <EditFolderNameTextField
                name={folder.name}
                folderId={folder.id}
                setIsEditingName={setIsEditingName}
              />
            ) : (
              <Typography variant="h3" noWrap>
                {folder.name}
              </Typography>
            )}
          </Box>
          {!hideMoreActionButton && (
            <>
              <IconButton id="folder-more-icon-button" onClick={handleMenuOpen} size="small">
                <MoreHorizIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                onClick={e => e.stopPropagation()}
              >
                <MenuItem onClick={handleRename}>{t('Rename')}</MenuItem>
                {!hideMove && <MenuItem onClick={handleMove}>{t('Move to...')}</MenuItem>}
                <MenuItem onClick={handleDelete}>
                  <Typography color="secondary" className={styles.deleteText}>
                    {t('Delete')}
                  </Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        <Box className={styles.createdByRow}>
          <Tooltip title={t('This folder is visible to all users within your organization.')} arrow>
            <GroupsIcon className={styles.createdByIcon} />
          </Tooltip>
          <Typography variant="body1" className={styles.textColor}>
            {creatorUser
              ? t('Created by {{creatorName}}', {
                  creatorName: isCurrentUserCreator ? t('You') : creatorUser.name,
                })
              : t('Folder')}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default FolderCard;
